import { useState, useEffect } from 'react';
import {
  Star, MessageSquare, TrendingUp, Minus,
  Loader2, RefreshCw, Trash2, ChevronDown, ChevronUp,
  Filter, Calendar, Users, ThumbsUp, ThumbsDown
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import adminApi from '../../services/adminApi';

import { PageTitle, StatsCard } from './shared';

const EXPERIENCE_COLORS = {
  excellent: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
  good: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
  average: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300',
  poor: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300',
};

const EXPERIENCE_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  average: 'Average',
  poor: 'Poor',
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const StarRating = ({ rating, size = 'sm' }) => {
  const sizeClass = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

const NPSBadge = ({ score }) => {
  let color, label, Icon;
  if (score >= 9) {
    color = 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
    label = 'Promoter';
    Icon = ThumbsUp;
  } else if (score >= 7) {
    color = 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
    label = 'Passive';
    Icon = Minus;
  } else {
    color = 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
    label = 'Detractor';
    Icon = ThumbsDown;
  }

  return (
    <Badge className={color}>
      <Icon className="w-3 h-3 mr-1" />
      {score}/10 ({label})
    </Badge>
  );
};

const FeedbackRow = ({ feedback, onDelete, expanded, onToggleExpand }) => {
  return (
    <div className="border-b border-gray-100 dark:border-gray-800 last:border-0">
      <div
        className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <StarRating rating={feedback.rating} />
            <Badge className={EXPERIENCE_COLORS[feedback.experience_quality] || 'bg-gray-100 dark:bg-gray-800'}>
              {EXPERIENCE_LABELS[feedback.experience_quality] || feedback.experience_quality}
            </Badge>
            <NPSBadge score={feedback.recommend_likelihood} />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(feedback.submitted_at)}</span>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        {feedback.program && (
          <div className="mt-2">
            <Badge variant="outline" className="text-xs">
              <Users className="w-3 h-3 mr-1" />
              {feedback.program}
            </Badge>
          </div>
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="grid md:grid-cols-2 gap-4">
            {feedback.what_you_like && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">What they liked:</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 bg-background p-3 rounded-lg border">
                  {feedback.what_you_like}
                </p>
              </div>
            )}
            {feedback.suggestions && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Suggestions:</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 bg-background p-3 rounded-lg border">
                  {feedback.suggestions}
                </p>
              </div>
            )}
          </div>

          {!feedback.what_you_like && !feedback.suggestions && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">No additional comments provided</p>
          )}

          <div className="mt-3 flex justify-between items-center">
            <span className="text-xs text-gray-400">
              Source: {feedback.source || 'qr_code'}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(feedback);
              }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const FeedbackDashboard = () => {
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingFeedback, setDeletingFeedback] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    rating: 'all',
    experience: 'all',
    days: '30',
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadStats = async () => {
    try {
      const response = await adminApi.get(`/api/admin/feedback/stats?days=${filters.days}`);
      if (!response.ok) throw new Error('Failed to load stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadFeedback = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20',
      });

      if (filters.rating !== 'all') {
        params.append('rating', filters.rating);
      }
      if (filters.experience !== 'all') {
        params.append('experience', filters.experience);
      }
      if (filters.days !== 'all') {
        params.append('days', filters.days);
      }

      const response = await adminApi.get(`/api/admin/feedback?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to load feedback');
      const data = await response.json();
      setFeedback(data.items || []);
      setTotalPages(data.total_pages || 1);
      setError(null);
    } catch (err) {
      setError('Failed to load feedback');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    loadFeedback();
  }, [filters, page]);

  const handleDelete = async () => {
    if (!deletingFeedback) return;

    try {
      const response = await adminApi.delete(`/api/admin/feedback/${deletingFeedback.id}`);
      if (!response.ok) throw new Error('Failed to delete');
      await loadFeedback();
      await loadStats();
      setDeletingFeedback(null);
    } catch (err) {
      console.error('Failed to delete feedback:', err);
      alert('Failed to delete feedback');
    }
  };

  const handleRefresh = () => {
    loadStats();
    loadFeedback();
  };

  const getNPSColor = (score) => {
    if (score >= 50) return 'text-green-600';
    if (score >= 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading && feedback.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Feedback Dashboard"
        description="View and analyze customer feedback submissions"
        icon={<MessageSquare className="h-6 w-6" />}
        actions={
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        }
      />

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="NPS Score"
            value={stats.nps_score}
            subtitle={`${stats.promoters} promoters, ${stats.detractors} detractors`}
            icon={TrendingUp}
            color={getNPSColor(stats.nps_score)}
          />
          <StatsCard
            title="Average Rating"
            value={`${stats.average_rating.toFixed(1)}/5`}
            subtitle={
              <StarRating rating={Math.round(stats.average_rating)} />
            }
            icon={Star}
          />
          <StatsCard
            title="Total Submissions"
            value={stats.total_submissions}
            subtitle={`Last ${stats.recent_period_days} days`}
            icon={MessageSquare}
          />
          <StatsCard
            title="Avg Recommendation"
            value={`${stats.average_nps_score.toFixed(1)}/10`}
            subtitle="How likely to recommend"
            icon={Users}
          />
        </div>
      )}

      {/* NPS Breakdown */}
      {stats && stats.total_submissions > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">NPS Breakdown</h3>
            <div className="flex items-center gap-2 h-8 rounded-lg overflow-hidden">
              {stats.promoters > 0 && (
                <div
                  className="bg-green-500 h-full flex items-center justify-center text-white text-sm font-medium"
                  style={{ width: `${(stats.promoters / stats.total_submissions) * 100}%` }}
                >
                  {stats.promoters}
                </div>
              )}
              {stats.passives > 0 && (
                <div
                  className="bg-yellow-500 h-full flex items-center justify-center text-white text-sm font-medium"
                  style={{ width: `${(stats.passives / stats.total_submissions) * 100}%` }}
                >
                  {stats.passives}
                </div>
              )}
              {stats.detractors > 0 && (
                <div
                  className="bg-red-500 h-full flex items-center justify-center text-white text-sm font-medium"
                  style={{ width: `${(stats.detractors / stats.total_submissions) * 100}%` }}
                >
                  {stats.detractors}
                </div>
              )}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-green-500 rounded"></span>
                Promoters (9-10)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-yellow-500 rounded"></span>
                Passives (7-8)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-500 rounded"></span>
                Detractors (0-6)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Experience Breakdown */}
      {stats && Object.keys(stats.experience_breakdown || {}).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Experience Quality</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.experience_breakdown).map(([quality, count]) => (
                <Badge key={quality} className={`${EXPERIENCE_COLORS[quality] || 'bg-gray-100 dark:bg-gray-800'} text-sm`}>
                  {EXPERIENCE_LABELS[quality] || quality}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>

            <Select
              value={filters.days}
              onValueChange={(value) => {
                setFilters({ ...filters, days: value });
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.rating}
              onValueChange={(value) => {
                setFilters({ ...filters, rating: value });
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[130px]">
                <Star className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ratings</SelectItem>
                <SelectItem value="5">5 stars</SelectItem>
                <SelectItem value="4">4 stars</SelectItem>
                <SelectItem value="3">3 stars</SelectItem>
                <SelectItem value="2">2 stars</SelectItem>
                <SelectItem value="1">1 star</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.experience}
              onValueChange={(value) => {
                setFilters({ ...filters, experience: value });
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All experiences</SelectItem>
                <SelectItem value="excellent">Excellent</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="average">Average</SelectItem>
                <SelectItem value="poor">Poor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Feedback List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Submissions ({feedback.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {feedback.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {feedback.map((item) => (
                <FeedbackRow
                  key={item.id}
                  feedback={item}
                  expanded={expandedId === item.id}
                  onToggleExpand={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  onDelete={setDeletingFeedback}
                />
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No Feedback Yet</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Feedback submissions will appear here when customers submit them.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-3 text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingFeedback} onOpenChange={(open) => !open && setDeletingFeedback(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Feedback</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this feedback submission? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-gradient-to-r from-red-800 to-red-900 text-amber-50 border border-amber-600/30 hover:from-red-700 hover:to-red-800"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeedbackDashboard;
